import { Alert, Link } from "@mui/material";
import ExpandableTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import { format } from "date-fns";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import {
  ActorKind,
  ResponseCode,
  ListRequestOfUserSkuActivitiesFilter,
  TimelineActor,
  TimelineSource,
  UserSkuActivitiesFilter,
  UserSkuAuditTimelineEntry,
} from "services/KnowfullyAdminClient";
import { standardDateTimeWithSecondsFormat } from "utils/dateUtils";

type UserSkuAuditTimelineTableProps = {
  userSkuId: string;
};

const styles = {
  expandedRows: {
    padding: "8px 16px",
  },
  expandedRow: {
    padding: "2px 0",
  },
  partialWarning: {
    marginBottom: "8px",
  },
};

const UserSkuAuditTimelineTable = ({
  userSkuId,
}: UserSkuAuditTimelineTableProps) => {
  const { formatMessage: t } = useIntl();
  const { userSkuService, userDetailsUrl } = useGlobalContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("date");

  const request = ListRequestOfUserSkuActivitiesFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: UserSkuActivitiesFilter.fromJS({
      userSkuId,
    }),
  });

  const result = useStrictQuery(
    ["userSkuService.getUserSkuAuditTimeline", request],
    () => userSkuService.getUserSkuAuditTimeline(request),
    {
      enabled: !!userSkuId,
    }
  );

  const ActorView = ({ actor }: { actor?: TimelineActor }) => {
    if (!actor) return null;

    // The server leaves the name unset when it could not identify anyone, so the label is localized here
    // rather than shipped as English from the API.
    const displayName = actor.displayName || t({ id: "unknownActor" });

    const isLinkable =
      (actor.kind === ActorKind.User || actor.kind === ActorKind.Admin) &&
      !!actor.organizationUserId;

    if (!isLinkable) {
      return <span>{displayName}</span>;
    }

    return (
      <Link
        component={RouterLink}
        to={userDetailsUrl(actor.organizationUserId)}
      >
        {displayName}
      </Link>
    );
  };

  const cells: TableCellType[] = [
    {
      label: t({ id: "activityDate" }),
      accessor: "date",
      type: "node",
      View: (row: { value: UserSkuAuditTimelineEntry }) => {
        if (!row.value.date) return null;
        return (
          <span>
            {format(row.value.date, standardDateTimeWithSecondsFormat)}
          </span>
        );
      },
    },
    {
      label: t({ id: "auditEvent" }),
      accessor: "event",
      type: "text",
      noSort: true,
    },
    {
      label: t({ id: "actor" }),
      accessor: "actor",
      type: "node",
      noSort: true,
      View: (row: { value: UserSkuAuditTimelineEntry }) => (
        <ActorView actor={row.value.actor} />
      ),
    },
  ];

  // Only audit-log entries carry field-level deltas; the two activity streams have nothing to reveal, so their
  // rows stay flat rather than expanding into an empty panel.
  //
  // Memoised because the shared table re-mounts an expanded row whenever its props change identity, which
  // would collapse the panel under the reader on every refetch.
  const rows = useMemo(
    () =>
      (result.data?.data ?? []).map((entry) => ({
        ...entry,
        id: entry.id!,
        isExpandable:
          entry.source === TimelineSource.AuditLog && !!entry.rows?.length,
        renderExpandedData: () => (
          <div style={styles.expandedRows}>
            {(entry.rows ?? []).map((row, index) => (
              <div key={index} style={styles.expandedRow}>
                <strong>{row.name}</strong>
                {": "}
                {/* Not every audited row is a transition — some record a value that had no prior state, and
                    an arrow there would read as a change that never happened. */}
                {row.priorValue == null ? (
                  row.value
                ) : (
                  <>
                    {row.priorValue}
                    {" → "}
                    {row.value ?? t({ id: "notSet" })}
                  </>
                )}
              </div>
            ))}
          </div>
        ),
      })),
    [result.data?.data, t]
  );

  // Re-sorting reorders the whole merged set, so staying on page 5 would show an unrelated slice — or, past
  // the end, an empty table with a non-zero total.
  const handleSetOrder = (value: TableSortType) => {
    setPage(0);
    setOrder(value);
  };

  const isPartial =
    !!result.data?.responseCode && result.data.responseCode !== ResponseCode.OK;

  return (
    <>
      {isPartial && (
        <Alert severity="warning" sx={styles.partialWarning}>
          {result.data?.responseReason || t({ id: "auditTimelinePartial" })}
        </Alert>
      )}
      <ExpandableTable
        rows={rows}
        cells={cells}
        rowCount={result.data?.totalRecords ?? 0}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order={order}
        setOrder={handleSetOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        loading={result.isLoading}
        collapsable
        dense
      />
    </>
  );
};

export default withErrorBoundary(UserSkuAuditTimelineTable);
